import { useState } from "react";
import { Loader2, Trash2 } from "lucide-react";
import { Button } from "~/components/ui/button";
import {
  SearchIndexStatus,
  SearchIndexTypeStatus,
  SearchOrphanResponse,
  SearchOrphanTypeStatus,
  runSearchPrune,
} from "~/services/search";
import { ChunkSizes } from "./ChunkSizes";
import { CoverageMeter } from "./CoverageMeter";
import { Column, EntityTypeTable } from "./EntityTypeTable";
import { SampleLine } from "./SampleLine";
import { Section } from "./Section";
import { ENTITY_TYPE_LABEL, Fetched, errorMessage } from "./constants";

/** A figure this comparison could not honestly produce. */
const Withheld = () => <span className="font-normal text-muted-foreground">—</span>;

const formatTimestamp = (value: string | null) =>
  value ? new Date(value).toLocaleString() : "never";

type ContentsRow = SearchIndexTypeStatus & { orphan: SearchOrphanTypeStatus | undefined };

const COLUMNS: Column<ContentsRow>[] = [
  {
    label: "In source",
    hint: "Entities the source holds today.",
    cell: ({ orphan }) => (orphan ? orphan.in_source : <Withheld />),
  },
  { label: "Indexed", cell: (row) => row.entities },
  {
    label: "Coverage",
    hint: "How much of the source is in the index. Short of 100% means entities exist that search cannot return.",
    cell: ({ entities, orphan }) =>
      orphan ? <CoverageMeter indexed={entities} inSource={orphan.in_source} /> : <Withheld />,
  },
  {
    label: "Orphaned",
    hint: "The other direction: indexed entities the source no longer has, which is what a prune deletes. A dash means the source cannot prove an entity is gone, so nothing is counted or pruned.",
    isEmphasized: true,
    // A blocked type's source listed nothing, so its orphans are not a count of deletions.
    cell: ({ orphan }) => (orphan && !orphan.blocked ? orphan.orphans : <Withheld />),
  },
  { label: "Rows", cell: (row) => row.chunks },
  {
    label: "Rows per entity",
    hint: "Near 1 means the corpus is too short to chunk, so splitting it buys nothing.",
    cell: (row) => (row.entities > 0 ? (row.chunks / row.entities).toFixed(1) : "—"),
  },
  {
    label: "Last indexed",
    isText: true,
    cell: (row) => (
      <span className="text-xs text-muted-foreground">{formatTimestamp(row.indexed_at)}</span>
    ),
  },
];

/** What the index holds beside what its sources hold. */
export const IndexContents = ({
  status,
  orphans,
  onPruned,
}: {
  status: Fetched<SearchIndexStatus>;
  orphans: Fetched<SearchOrphanResponse>;
  /** Re-reads the status counts as well as the orphans. */
  onPruned: () => Promise<unknown>;
}) => {
  const [isPruning, setIsPruning] = useState(false);
  const [pruneError, setPruneError] = useState<string | null>(null);

  const orphanTypes = orphans.data?.types ?? [];
  const byType = new Map(orphanTypes.map((type) => [type.entity_type, type]));
  const deletable = orphanTypes.filter((type) => type.orphans > 0 && !type.blocked);
  const blocked = orphanTypes.filter((type) => type.blocked);

  const onPrune = async () => {
    setIsPruning(true);
    setPruneError(null);
    try {
      await runSearchPrune();
      await onPruned();
    } catch (caught) {
      setPruneError(errorMessage(caught));
    } finally {
      setIsPruning(false);
    }
  };

  return (
    <Section
      title="Index contents"
      action={
        deletable.length > 0 && (
          <Button size="sm" variant="outline" onClick={onPrune} disabled={isPruning}>
            {isPruning ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Trash2 className="size-4" />
            )}
            Prune
          </Button>
        )
      }
    >
      {status.error && (
        <p className="text-sm text-destructive">
          Could not read the index: {errorMessage(status.error)}
        </p>
      )}
      {status.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {orphans.error && (
        <p className="text-sm text-destructive">
          Could not compare against the sources: {errorMessage(orphans.error)}
        </p>
      )}
      {pruneError && <p className="text-sm text-destructive">Prune failed: {pruneError}</p>}
      {status.data && (
        <>
          <EntityTypeTable
            label="Index contents"
            rows={status.data.types.map((type) => ({
              ...type,
              orphan: byType.get(type.entity_type),
            }))}
            columns={COLUMNS}
          />
          <div className="flex flex-col gap-1">
            {blocked.map((type) => (
              <p key={type.entity_type} className="text-xs text-muted-foreground">
                {ENTITY_TYPE_LABEL[type.entity_type]} cannot be pruned: {type.blocked}.
              </p>
            ))}
            {deletable.map((type) => (
              <SampleLine
                key={type.entity_type}
                label={`${ENTITY_TYPE_LABEL[type.entity_type]} orphans`}
                names={type.sample}
                total={type.orphans}
              />
            ))}
          </div>
          <div className="flex flex-col gap-1">
            <h3 className="text-xs font-medium text-muted-foreground">Chunk length (characters)</h3>
            <ChunkSizes buckets={status.data.chunk_sizes} />
          </div>
        </>
      )}
    </Section>
  );
};
